/* Kathwool storefront localization: text only; no HTML, style, media or URL changes. */
(() => {
  'use strict';

  const script = document.currentScript;
  const locale = String(script?.dataset.kwLocale || document.documentElement.lang || '')
    .toLowerCase().split('-')[0];
  if (!['fr', 'de'].includes(locale) || !script?.dataset.kwDictionary) return;

  // The snippet only loads this file in French/German. This second guard also
  // makes an accidental English-page inclusion inert.
  const stateKey = '__kwTextLocalization';
  if (window[stateKey]) return;
  window[stateKey] = { locale, ready: false };

  const attributes = ['placeholder', 'aria-label', 'title'];
  const protectedSelector = [
    'script', 'style', 'template', 'noscript', 'iframe', 'object', 'embed',
    'math', 'canvas', 'img', 'picture', 'source', 'video', 'audio',
    'pre', 'code', 'textarea',
    '[contenteditable]:not([contenteditable="false"])',
    '[data-no-translate]',
    '[data-kw-localization-ignore]', '[data-kw-localization-lock]',
    'input[type="password"]', 'input[type="hidden"]',
    '[autocomplete^="cc-"]', '[data-payment]', '[data-credit-card]',
    'form[action*="/payments/"]', 'form[action*="/wallets/"]',
    '.shopify-payment-button', '[data-shopify="payment-button"]',
    '.additional-checkout-buttons', 'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart', 'shopify-payment-terms',
    'shopify-installments-modal',
    '#shopify-privacy-banner', 'shopify-privacy-banner',
    '[id^="shopify-pc__"]', '[class*="shopify-pc__"]',
    '[data-cookie-consent]', '[data-privacy-banner]',
  ].join(',');
  const learningRootSelector = '.kwj[id^="kw-mosaic-journal-"]';

  // These wrappers use translate="no" to disable browser machine translation,
  // not Kathwool's reviewed dictionary. Exempt only the owned wrapper itself;
  // explicit opt-outs on nested user/app content remain protected.
  const browserOptOutSelector = '[translate="no"], .notranslate';
  const ownedRootSelector = [
    '.kwj[id^="kw-mosaic-journal-"]',
    '[data-kw-xmas]', '[data-kwc-root]', '[data-kwp-fresh]', '[data-kwpf-footer]',
    '[id^="kw-about-"]', '[id^="kw-contact-"]', '[data-kw-owned-root]',
  ].join(',');

  const translatedText = new WeakMap();
  const translatedAttributes = new WeakMap();
  const contextualText = new WeakMap();
  const pending = new Set();
  let dictionary;
  let timer = 0;

  function isProtected(node) {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!element || element.closest(protectedSelector)) return true;
    const svg = element.closest('svg');
    if (svg && (!svg.closest(learningRootSelector)
      || !['svg', 'g', 'text', 'tspan'].includes(element.localName?.toLowerCase()))) return true;
    for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor.matches(browserOptOutSelector) && !ancestor.matches(ownedRootSelector)) return true;
    }
    return false;
  }

  function keepWhitespace(original, replacement) {
    const core = original.trim();
    const start = original.indexOf(core);
    return original.slice(0, start) + replacement + original.slice(start + core.length);
  }

  // Only these complete, anchored UI grammars accept variable values. Captured
  // numbers keep their original digits; this does not parse or format prices.
  function templateTranslation(core) {
    let match;
    if ((match = /^(\d+) patterns loaded$/.exec(core))) {
      return locale === 'fr' ? `${match[1]} modèles chargés` : `${match[1]} Anleitungen geladen`;
    }
    if ((match = /^(\d+) patterns?$/.exec(core))) {
      return locale === 'fr'
        ? `${match[1]} ${Number(match[1]) === 1 ? 'modèle' : 'modèles'}`
        : `${match[1]} ${Number(match[1]) === 1 ? 'Anleitung' : 'Anleitungen'}`;
    }
    if ((match = /^(\d+) guides?$/.exec(core))) {
      return locale === 'fr'
        ? `${match[1]} ${Number(match[1]) === 1 ? 'guide' : 'guides'}` : `${match[1]} Ratgeber`;
    }
    if ((match = /^Shopping bag, (\d+) items?$/.exec(core))) {
      return locale === 'fr'
        ? `Sac d’achat, ${match[1]} ${Number(match[1]) === 1 ? 'article' : 'articles'}`
        : `Einkaufstasche, ${match[1]} Artikel`;
    }
    if ((match = /^View image (\d+) of (\d+)$/.exec(core))) {
      return locale === 'fr' ? `Voir l’image ${match[1]} sur ${match[2]}`
        : `Bild ${match[1]} von ${match[2]} ansehen`;
    }
    if ((match = /^Read guide: (.+)$/.exec(core))) {
      const title = dictionary.get(match[1]) || match[1];
      return locale === 'fr' ? `Lire le guide : ${title}` : `Ratgeber lesen: ${title}`;
    }
    if ((match = /^Add (.+) to cart$/.exec(core))) {
      const title = dictionary.get(match[1]) || match[1];
      return locale === 'fr' ? `Ajouter ${title} au panier` : `${title} in den Warenkorb legen`;
    }
    return null;
  }

  // Exact whole-string lookup, then a trimmed lookup or one of the dedicated UI
  // templates above. No fuzzy replacements, generated HTML or expression eval.
  function translate(value) {
    if (dictionary.has(value)) return dictionary.get(value);
    const core = value.trim();
    if (!core) return value;
    const target = dictionary.get(core) || templateTranslation(core);
    return target === null || target === undefined ? value : keepWhitespace(value, target);
  }

  function heroTranslation(node, original) {
    const span = node.parentElement;
    const heading = span?.parentElement;
    if (span?.localName !== 'span' || !heading?.matches('h1[id$="-hero-title"]')
      || !heading.closest('[data-kw-xmas]')) return null;
    const known = contextualText.get(node);
    if (known?.heading === heading && known.source === original.trim()) {
      return keepWhitespace(original, known.target);
    }
    const spans = Array.from(heading.children);
    if (spans.length !== 3 || spans.some((part) => part.localName !== 'span')) return null;
    const nodes = spans.map((part) => Array.from(part.childNodes)
      .filter((child) => child.nodeType === Node.TEXT_NODE && child.nodeValue.trim()));
    if (nodes.some((parts) => parts.length !== 1)) return null;
    const sources = nodes.map((parts) => parts[0].nodeValue.trim());
    if (sources.join('|') !== 'A handmade|Christmas|starts with you.') return null;
    const targets = locale === 'fr'
      ? ['Un Noël', 'fait main', 'commence avec vous.']
      : ['Ein handgemachtes', 'Weihnachten', 'beginnt mit Ihnen.'];
    nodes.forEach((parts, index) => contextualText.set(parts[0], {
      source: sources[index], target: targets[index], heading,
    }));
    return keepWhitespace(original, contextualText.get(node).target);
  }

  function visibleLearningText(element) {
    if (!element.closest('svg')) return true;
    if (!element.closest('text, tspan') || element.closest('[hidden]')) return false;
    const style = window.getComputedStyle?.(element);
    if (style && (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility))) return false;
    return typeof element.getClientRects !== 'function' || element.getClientRects().length > 0;
  }

  function processText(node) {
    if (isProtected(node)) return;
    const original = node.nodeValue;
    if (!original?.trim() || translatedText.get(node) === original) return;
    if (!visibleLearningText(node.parentElement)) return;
    // SVG captions require an exact dictionary hit; variable templates are for
    // UI strings only. All SVG geometry and all SVG attributes stay untouched.
    const result = node.parentElement.closest('svg')
      ? (dictionary.has(original.trim()) ? keepWhitespace(original, dictionary.get(original.trim())) : original)
      : (heroTranslation(node, original) ?? translate(original));
    if (result === original) return;
    translatedText.set(node, result);
    node.nodeValue = result;
  }

  function processAttributes(element) {
    if (isProtected(element) || element.closest('svg')) return;
    for (const name of attributes) {
      const original = element.getAttribute(name);
      if (!original || translatedAttributes.get(element)?.[name] === original) continue;
      const result = translate(original);
      if (result === original) continue;
      const record = translatedAttributes.get(element) || Object.create(null);
      record[name] = result;
      translatedAttributes.set(element, record);
      element.setAttribute(name, result);
    }
  }

  function walk(root) {
    if (!root?.isConnected || isProtected(root)) return;
    if (root.nodeType === Node.TEXT_NODE) {
      processText(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    processAttributes(root);
    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (node.nodeType === Node.ELEMENT_NODE && isProtected(node)) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.nodeType === Node.TEXT_NODE) processText(node);
      else processAttributes(node);
    }
  }

  function flush() {
    timer = 0;
    const start = performance.now();
    // Work only on changed subtrees, not the whole page on every timer tick.
    for (const node of pending) {
      pending.delete(node);
      walk(node);
      if (performance.now() - start > 12) break;
    }
    if (pending.size) timer = window.setTimeout(flush, 40);
  }

  function enqueue(node) {
    if (!node?.isConnected || isProtected(node)) return;
    if (pending.has(document.body)) return;
    if (node === document.body) pending.clear();
    pending.add(node);
    // A bounded throttle keeps live cart messages and inserted FAQ text current
    // without continuous rescanning or a debounce that can starve under updates.
    if (!timer) timer = window.setTimeout(flush, 40);
  }

  function start() {
    if (!document.body) return;
    walk(document.body);
    const observer = new MutationObserver((changes) => {
      for (const change of changes) {
        if (change.type === 'characterData') {
          if (translatedText.get(change.target) !== change.target.nodeValue) enqueue(change.target);
        } else if (change.type === 'attributes') {
          const record = translatedAttributes.get(change.target);
          if (record?.[change.attributeName] !== change.target.getAttribute(change.attributeName)) {
            enqueue(change.target);
          }
        } else {
          for (const node of change.addedNodes) enqueue(node);
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: attributes,
    });
    document.addEventListener('shopify:section:load', (event) => enqueue(event.target));
    document.addEventListener('toggle', (event) => enqueue(event.target), true);
    window.addEventListener('pageshow', () => enqueue(document.body));
    window[stateKey].ready = true;
  }

  function startAfterThemeInit() {
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      // Complete the DOMContentLoaded dispatch, its microtasks and the existing
      // initializers' first task before changing their English label inputs.
      window.setTimeout(() => window.setTimeout(start, 0), 0);
    };
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    if (document.readyState === 'complete' || navigation?.domContentLoadedEventStart > 0) {
      schedule();
    } else {
      document.addEventListener('DOMContentLoaded', schedule, { once: true });
      // Covers a late-inserted script where navigation timing is unavailable
      // and DOMContentLoaded already fired while resources are still loading.
      window.addEventListener('load', schedule, { once: true });
    }
  }

  async function load() {
    try {
      const ownOrigin = new URL(script.src, location.href).origin;
      const url = new URL(script.dataset.kwDictionary, location.href);
      // Only the theme asset specified by Liquid is read; no external translation
      // service, crawl, form submission, customer-data access or checkout access.
      if (!['https:', 'http:'].includes(url.protocol)
        || ![ownOrigin, location.origin].includes(url.origin)) return;
      const response = await fetch(url.href, {
        credentials: 'omit', cache: 'force-cache', redirect: 'error',
      });
      if (!response.ok) throw new Error('Dictionary asset unavailable');
      const payload = await response.json();
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
      dictionary = new Map();
      for (const [source, entry] of Object.entries(payload)) {
        const target = typeof entry === 'string' ? entry : entry?.[locale];
        if (source && typeof target === 'string' && target) dictionary.set(source, target);
      }
      if (!dictionary.size) return;
      startAfterThemeInit();
    } catch (_error) {
      // Failure leaves the original storefront text and all functionality intact.
      window[stateKey].ready = false;
    }
  }

  load();
})();
